import { DayOfWeek } from "@redotech/crdb-enums/inventory/day-of-week";
import { getAvailableTimezones } from "@redotech/redo-common-model/timezone-utils";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { memo, useCallback, useMemo, useState } from "react";
import { RedoButton } from "../arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "../arbiter-components/checkbox/redo-checkbox";
import { RedoSingleSelectDropdownInput } from "../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import type { TimeOfDay } from "../arbiter-components/time/redo-time-picker";
import {
  RedoTimeRangePicker,
  type TimeOfDayRange,
} from "../arbiter-components/time/redo-time-range-picker";
import { Flex } from "../flex";
import { Text } from "../text";
import * as styles from "./weekly-schedule-editor.module.css";

/** Week order for display; the values themselves come from the DB enum. */
export const DAYS = [
  DayOfWeek.Sunday,
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
  DayOfWeek.Saturday,
] as const;

export type DayName = (typeof DAYS)[number];

export interface TimeBlock {
  start?: string | undefined;
  end?: string | undefined;
}

export interface DaySchedule {
  enabled: boolean;
  timeBlocks: TimeBlock[];
}

export type WeeklyScheduleValue = Partial<Record<DayName, DaySchedule>>;

const DEFAULT_BLOCK: TimeBlock = { start: "09:00", end: "17:00" };

export function convertStringToTimeOfDay(
  timeString: string | undefined,
): TimeOfDay | null {
  if (!timeString) {
    return null;
  }
  const parts = timeString.split(":").map(Number);
  return { hours: parts[0] ?? 0, minutes: parts[1] ?? 0 };
}

export function convertTimeOfDayToString(timeOfDay: TimeOfDay): string {
  return `${timeOfDay.hours.toString().padStart(2, "0")}:${timeOfDay.minutes.toString().padStart(2, "0")}`;
}

export function convertStringRangeToTimeOfDayRange(
  start: string | undefined,
  end: string | undefined,
): TimeOfDayRange | null {
  const startTime = convertStringToTimeOfDay(start || "09:00");
  const endTime = convertStringToTimeOfDay(end || "17:00");
  if (!startTime || !endTime) {
    return null;
  }
  return { start: startTime, end: endTime };
}

export const TimezoneSelector = memo(function TimezoneSelector({
  timezone,
  onChange,
  disabled,
  label,
}: {
  timezone?: string;
  onChange: (tz: string) => void;
  disabled?: boolean;
  label?: string;
}) {
  const [searchString, setSearchString] = useState("");

  const timezoneOptions = useMemo(() => {
    const query = searchString.toLowerCase().trim();
    return getAvailableTimezones()
      .filter((tz) => tz.toLowerCase().includes(query))
      .map((tz) => ({ id: tz, type: "text" as const, text: tz, value: tz }));
  }, [searchString]);

  /** Built from the current value, not the filtered list, so a search that
   *  hides the selected zone does not blank the input. */
  const selectedTimezone = useMemo(
    () =>
      timezone
        ? {
            id: timezone,
            type: "text" as const,
            text: timezone,
            value: timezone,
          }
        : undefined,
    [timezone],
  );

  return (
    <RedoSingleSelectDropdownInput
      disabled={disabled}
      label={label}
      options={timezoneOptions}
      optionSelected={({ item }) => onChange(item.value)}
      placeholder="Select timezone"
      searchPlaceholder="Search timezones..."
      searchString={searchString}
      selectedItem={selectedTimezone}
      setSearchString={setSearchString}
    />
  );
});

/** A day's list of time blocks, with add/remove. Shared by weekly hours and date overrides. */
export const TimeBlocksEditor = memo(function TimeBlocksEditor({
  value,
  onChange,
  disabled,
  align = "start",
}: {
  value: readonly TimeBlock[];
  onChange: (blocks: TimeBlock[]) => void;
  disabled?: boolean;
  /** "end" pins the controls to the row's right edge, as the day rows do. */
  align?: "start" | "end";
}) {
  const handleChange = useCallback(
    (blockIndex: number, range: TimeOfDayRange | null) => {
      onChange(
        value.map((block, i) =>
          i === blockIndex
            ? {
                start: range
                  ? convertTimeOfDayToString(range.start)
                  : undefined,
                end: range ? convertTimeOfDayToString(range.end) : undefined,
              }
            : block,
        ),
      );
    },
    [onChange, value],
  );

  const handleAdd = useCallback(() => {
    onChange([...value, { ...DEFAULT_BLOCK }]);
  }, [onChange, value]);

  const handleRemove = useCallback(
    (blockIndex: number) => {
      onChange(value.filter((_, i) => i !== blockIndex));
    },
    [onChange, value],
  );

  // An off day still shows a placeholder row so the grid doesn't jump on toggle.
  const rows = value.length > 0 ? value : [{ ...DEFAULT_BLOCK }];

  return (
    <Flex dir="column" gap="lg" grow={1}>
      {rows.map((block, i) => (
        <Flex
          align="center"
          gap="sm"
          justify={align === "end" ? "flex-end" : "flex-start"}
          key={i}
          w="full"
        >
          <div
            aria-disabled={disabled}
            className={disabled ? styles.pickerDisabled : undefined}
          >
            <RedoTimeRangePicker
              clearIcon={null}
              setValue={(range: TimeOfDayRange | null) =>
                handleChange(i, range)
              }
              value={convertStringRangeToTimeOfDayRange(block.start, block.end)}
            />
          </div>
          {/*
           * One control column so add and remove line up: the first row adds,
           * the rest remove. The first block is cleared by unchecking the day.
           * Both buttons are the same size, so rows never shift.
           */}
          {i === 0 ? (
            <RedoButton
              disabled={disabled}
              hierarchy="tertiary"
              IconLeading={PlusIcon}
              onClick={handleAdd}
              size="sm"
            />
          ) : (
            <RedoButton
              disabled={disabled}
              hierarchy="tertiary"
              IconLeading={TrashIcon}
              onClick={() => handleRemove(i)}
              size="sm"
            />
          )}
        </Flex>
      ))}
    </Flex>
  );
});

/** Per-day toggle plus any number of time blocks. Shared by rules and user schedules. */
export const WeeklyScheduleEditor = memo(function WeeklyScheduleEditor({
  value,
  onChange,
  disabled,
}: {
  value: WeeklyScheduleValue;
  onChange: (value: WeeklyScheduleValue) => void;
  disabled?: boolean;
}) {
  const dayOf = useCallback(
    (day: DayName): DaySchedule =>
      value[day] ?? { enabled: false, timeBlocks: [] },
    [value],
  );

  const setDay = useCallback(
    (day: DayName, daySchedule: DaySchedule) => {
      onChange({ ...value, [day]: daySchedule });
    },
    [onChange, value],
  );

  const handleDayToggle = useCallback(
    (day: DayName, enabled: boolean) => {
      const daySchedule = dayOf(day);
      setDay(day, {
        ...daySchedule,
        enabled,
        timeBlocks:
          enabled && daySchedule.timeBlocks.length === 0
            ? [{ ...DEFAULT_BLOCK }]
            : daySchedule.timeBlocks,
      });
    },
    [dayOf, setDay],
  );

  const handleBlocksChange = useCallback(
    (day: DayName, timeBlocks: TimeBlock[]) => {
      const daySchedule = dayOf(day);
      // An off day still renders a placeholder row, and the time picker emits
      // that placeholder on mount. Enabling here would silently switch the day
      // on, with no hours behind it, before anyone touched the form.
      setDay(day, {
        ...daySchedule,
        enabled: timeBlocks.length > 0 || daySchedule.enabled,
        timeBlocks,
      });
    },
    [dayOf, setDay],
  );

  return (
    <Flex dir="column" gap="lg" w="full">
      {DAYS.map((day) => {
        const daySchedule = dayOf(day);
        return (
          <Flex align="flex-start" dir="row" gap="sm" key={day} w="full">
            <Flex align="center" className={styles.dayLabel} gap="sm">
              <RedoCheckbox
                disabled={disabled}
                setValue={(enabled) => handleDayToggle(day, enabled === true)}
                size="sm"
                value={daySchedule.enabled}
              />
              <Text fontSize="sm">
                {day.charAt(0).toUpperCase() + day.slice(1)}
              </Text>
            </Flex>
            <TimeBlocksEditor
              align="end"
              disabled={disabled || !daySchedule.enabled}
              onChange={(blocks) => handleBlocksChange(day, blocks)}
              value={daySchedule.timeBlocks}
            />
          </Flex>
        );
      })}
    </Flex>
  );
});
